import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import {
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { auth, db } from "../firebase";
import PlanYourTripModal from "./PlanYourTripModal";
import { Timestamp } from "firebase/firestore";
import { formatINR } from "../utils/currency";
import GoToTop from "../GoToTop";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import {
  addDoc,
  doc,
  serverTimestamp,
  collection,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { FaPencilAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import sedan from "./assets/images/cars/swift-dzire.webp";
import premium from "./assets/images/cars/toyota-innova.webp";
import standard from "./assets/images/cars/maruti_ertiga.webp";
import { RiUser6Line } from "react-icons/ri";
import {
  RAZORPAY_KEY,
  SMS_BASE_URL,
  SMS_FROM_NUMBER,
  SMS_API_KEY,MAP_KEY
} from "../API_KEYS";

function ChooseCar() {
  const location = useLocation();
  const navigate = useNavigate();

  let min_economyprice = 20;
  let max_economyprice = 35;

  let min_standardprice = 30;
  let max_standardprice = 40;

  let min_premiumprice = 38;
  let max_premiumprice = 51;

  const [directionsResult, setDirectionsResult] = useState(null);

  const pickup = location.state?.pickup;
  const drop = location.state?.drop;

  const isScheduled = location.state?.isScheduled;
  const pickupDate = location.state?.pickupDate;
  const pickupTime = location.state?.pickupTime;

  // console.log(pickupDate, "ddd")
  const [showEditModal, setShowEditModal] = useState(false);
  const [updatingTrip, setUpdatingTrip] = useState(false);
  const [loading, setLoading] = useState(false);

  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropCoords, setDropCoords] = useState(null);

  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);

  const [selectedCar, setSelectedCar] = useState(null);

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [user, setUser] = useState(null);
  const [customerName, setCustomerName] = useState(user?.name || "");
  const [customerPhone, setCustomerPhone] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [savingBooking, setSavingBooking] = useState(false);

  const [selectedRate, setSelectedRate] = useState(null);

  const predefinedLocations = {
    dhemaji: [27.4721, 94.5588],
    konsha: [26.677, 95.3193],
    "dibrugarh central inn hotel": [27.473597, 94.911000],
  };

  useEffect(() => {
    if (!pickup || !drop) return;

    const fetchMatchingRate = async () => {
      const pickupText = pickup.toLowerCase();
      const dropText = drop.toLowerCase();

      // determine which place to search
      let searchPlace = "";

      if (pickupText.includes("dibrugarh airport")) {
        searchPlace = drop; // airport -> city
      } else if (dropText.includes("dibrugarh airport")) {
        searchPlace = pickup; // city -> airport
      }

      if (!searchPlace) {
        setSelectedRate(null);
        return;
      }

      const { data, error } = await supabase
        .from("Rates")
        .select("*")
        .ilike("place", `%${searchPlace}%`)
        .limit(1)
        .single();

      if (error) {
        console.log("No matching rate found");
        setSelectedRate(null);
        return;
      }

      setSelectedRate(data);
    };

    if (isAirportRoute()) {
      fetchMatchingRate();
    } else {
      setSelectedRate(null);
    }
  }, [pickup, drop]);

  // AUTH USER
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) return setUser(null);

      try {
        const userDocRef = doc(db, "Users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setUser(userData);
          setCustomerName(userData?.name || "");
          setCustomerPhone(userData?.phone || "");
        } else {
          setUser(currentUser);
          setCustomerName(currentUser?.displayName || "");
          setCustomerPhone(currentUser?.phoneNumber || "");
        }
      } catch {
        setUser(currentUser);
      }
    });

    return () => unsub();
  }, []);

  const AIRPORT_NAME = "dibrugarh airport";

  const isAirportRoute = () => {
    const pickupText = (pickup || "").toLowerCase();
    const dropText = (drop || "").toLowerCase();

    return pickupText.includes(AIRPORT_NAME) || dropText.includes(AIRPORT_NAME);
  };

  // Convert address to coords using Google Maps
  const getCoords = async (place, setter) => {
    try {
      const placeKey = place.toLowerCase().trim();

      // ✅ 1. Check predefined locations first
      for (const key in predefinedLocations) {
        if (placeKey.includes(key)) {
          setter(predefinedLocations[key]);
          // console.log("Using predefined coords for:", key);
          return;
        }
      }

      // 🌍 2. Try OpenStreetMap
      const osmRes = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(place)}&format=json&limit=1`,
      );

      const osmData = await osmRes.json();

      if (osmData.length > 0) {
        setter([parseFloat(osmData[0].lat), parseFloat(osmData[0].lon)]);
        return;
      }

      // 🗺️ 3. Fallback Google Maps
      const googleRes = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(place)}&key=${MAP_KEY}`,
      );

      const googleData = await googleRes.json();

      if (googleData.status === "OK" && googleData.results.length > 0) {
        const location = googleData.results[0].geometry.location;
        setter([location.lat, location.lng]);
      } else {
        console.warn("No coordinates found for:", place);
      }
    } catch (err) {
      console.error("Geocode error:", err);
    }
  };

  const mapRef = useRef(null);

  useEffect(() => {
    if (pickup) getCoords(pickup, setPickupCoords);
    if (drop) getCoords(drop, setDropCoords);
  }, [pickup, drop]);

  //FETCH ROUTE
  const fetchRoute = async () => {
    if (!pickupCoords || !dropCoords) return;

    setLoading(true);

    try {
      const res = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${pickupCoords[1]},${pickupCoords[0]};${dropCoords[1]},${dropCoords[0]}?overview=full&geometries=geojson`,
      );

      const data = await res.json();

      const route = data.routes[0];

      const coords = route.geometry.coordinates.map((c) => [c[1], c[0]]);
      setDirectionsResult(coords);

      setDistance((route.distance / 1000).toFixed(2));
      setDuration((route.duration / 60).toFixed(1));
    } catch (err) {
      console.error("Route error", err);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchRoute();
  }, [pickupCoords, dropCoords]);

  // EDIT RIDE
  const handleEditRide = async (data) => {
    setUpdatingTrip(true);

    setTimeout(() => {
      navigate(
        `/ride/${data.pickup.toLowerCase().replace(/\s+/g, "-")}-to-${data.drop
          .toLowerCase()
          .replace(/\s+/g, "-")}`,
        {
          state: data,
          replace: true,
        },
      );

      setUpdatingTrip(false);
      setShowEditModal(false);
    }, 800);
  };

  const standard_price = Number(selectedRate?.economy || 0) + 1000;
  const premium_price = Number(selectedRate?.economy || 0) + 2000;

  const handleSelectCar = (type) => {
    let price = 0;
    let minprice = 0;

    if (selectedRate) {
      // Fixed rate (airport routes)
      price =
        type === "economy"
          ? selectedRate.economy
          : type === "standard"
            ? standard_price
            : premium_price;
    } else if (distance) {
      // Distance based pricing
      const km = parseFloat(distance);
      minprice =
        type === "economy"
          ? km * min_economyprice
          : type === "standard"
            ? km * min_standardprice
            : km * min_premiumprice;
      price =
        type === "economy"
          ? km * max_economyprice
          : type === "standard"
            ? km * max_standardprice
            : km * max_premiumprice;
    }

    if (!price) {
      toast.error("Price not available for this route");
      return;
    }

    let carData = null;

    if (type === "economy") {
      carData = {
        id: "economy",
        name: "Economy",
        seat: 5,
        price: Math.round(price),
        img_url: sedan,
        minprice: minprice,
        type: "economy",
        desc: "Affordable",
      };
    }

    if (type === "standard") {
      carData = {
        id: "standard",
        name: "Standard",
        seat: "6-7",
        price: Math.round(price),
        img_url: standard,
        minprice: minprice,
        type: "standard",
        desc: "Comfortable",
      };
    }

    if (type === "premium") {
      carData = {
        id: "premium",
        name: "Premium",
        seat: "6-7",
        price: Math.round(price),
        minprice: minprice,
        img_url: premium,
        type: "premium",
        desc: "Spacious",
      };
    }

    setSelectedCar(carData);
  };

  const PREBOOK_FEE = 200;

  const handlePreBookingPayment = async () => {
    if (!user) {
      toast.error("Please login first");
      return;
    }

    try {
      const options = {
        // key: "rzp_test_SEuHWyEAvt82zR", // TestAPI
        key: RAZORPAY_KEY, // Main Razorpay Key
        amount: PREBOOK_FEE * 100, // ₹200 in paise
        currency: "INR",
        name: "DibruGo",
        description: "Booking Fee",
        handler: async function (response) {
          // Razorpay Success Response
          const paymentId = response.razorpay_payment_id || "";

          toast.success("Payment successful!");

          // Call booking after payment success
          await handleConfirmBooking(paymentId);
        },
        prefill: {
          name: customerName,
          email: user?.email || "",
          contact: customerPhone,
        },
        theme: {
          color: "#0a0a0a",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      // console.error("Razorpay Error:", err);
      toast.error("Payment failed");
    }
  };

  let pickupTimestamp = null;

  if (isScheduled && pickupDate && pickupTime) {
    const dateTime = new Date(`${pickupDate}T${pickupTime}`);
    pickupTimestamp = Timestamp.fromDate(dateTime);
  }

  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toLocaleString("en-GB");
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp.seconds * 1000).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const sendSMSReminder = async (booking) => {
    const bookedAt = getCurrentDateTime(booking.created_at);
    const pickupAt = formatTimestamp(booking.pickupTime);

    const message = `🔔 New Booking (Pick & Drop)
     
Booking Time: ${bookedAt}
Pick Up Time: ${pickupAt}
  
Pickup: ${booking.pickup}
Drop: ${booking.drop}

Passengers: ${booking.passengers}
Price: ₹${booking.total_price}


Booking Details: https://dibrugo.com/admin-live-bookings`;

    try {
      const response = await fetch(SMS_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": SMS_API_KEY, // 👈 paste your key here
        },
        body: JSON.stringify({
          from: SMS_FROM_NUMBER, // 👈 put SIM number inside Android phone
          to: "+917576825040",
          content: message,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // toast.success("SMS Sent Successfully");
        console.log("SMS Response sent");
      } else {
        // toast.error("SMS Failed");
        console.log("Error:", data);
      }
    } catch (error) {
      console.error("SMS Error:", error);
      // toast.error("SMS Error");
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "0 mins";

    const totalMinutes = Math.round(parseFloat(minutes));
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (hrs > 0 && mins > 0) return `${hrs} hr ${mins} mins`;
    if (hrs > 0) return `${hrs} hr`;
    return `${mins} mins`;
  };

  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);

  const [loadingOTP, setLoadingOTP] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);

  const [timer, setTimer] = useState(0);

  // ⏳ Timer countdown
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Setup reCAPTCHA
  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
        },
      );
    }
  };

  // 📩 Send OTP
  const sendOTP = async () => {
    if (!customerPhone) return alert("Enter phone number");

    // 👉 Ensure +91 is added
    let formattedPhone = customerPhone.trim();

    if (!formattedPhone.startsWith("+91")) {
      formattedPhone = "+91" + formattedPhone;
    }

    try {
      setLoadingOTP(true);
      setupRecaptcha();

      const appVerifier = window.recaptchaVerifier;

      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        appVerifier,
      );

      setConfirmationResult(confirmation);
      window.confirmationResult = confirmation;

      setTimer(60); // ⏳ start 60 sec timer

      toast.success("OTP Sent!");
      setOtpSent(true);
    } catch (error) {
      console.error("OTP Error:", error);
    } finally {
      setLoadingOTP(false);
    }
  };

  // 🔐 Verify OTP
  const verifyOTP = async () => {
    if (!confirmationResult) {
      toast.error("Please request OTP first");
      return;
    }

    try {
      setLoadingVerify(true);

      const result = await confirmationResult.confirm(otp);

      const user = result.user;

      // Reference to Users collection
      const userRef = doc(db, "Users", user.uid);

      // Check if user already exists
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // Save new user
        await setDoc(userRef, {
          uid: user.uid,
          phone: user.phoneNumber,
          createdAt: new Date(),
        });
      }

      toast.success("Login Successful!");
      setPhoneVerified(true);
      setOtpSent(false);
    } catch (error) {
      console.error("Invalid OTP", error);
      toast.error("Invalid OTP");
    } finally {
      setLoadingVerify(false);
    }
  };
  // console.log(user?.phone,"jiji")


  // CONFIRM BOOKING
  const handleConfirmBooking = async (paymentRef = "") => {
     if (savingBooking) return; // 🔥 prevents double trigger

    if (typeof paymentRef !== "string") {
      paymentRef = ""; // prevent event object being stored
    }

    if (!customerName || !customerPhone || !selectedCar) {
      toast.error("Missing details");
      return;
    }

    if (!pickupCoords) {
      toast.error("Pickup coordinates not loaded yet");
      return;
    }

    if (!dropCoords) {
      toast.error("Drop coordinates not loaded yet");
      return;
    }

    setSavingBooking(true);

    try {
      const latitude = pickupCoords[0];
      const longitude = pickupCoords[1];

      const drop_latitude = dropCoords[0];
      const drop_longitude = dropCoords[1];

      // ✅ Update logged-in user details in Users collection
      if (user?.uid) {
        const userRef = doc(db, "Users", user.uid);

        await setDoc(
          userRef,
          {
            name: customerName,
          },
          { merge: true },
        );
      }

      const bookingData = {
        customer_id: user?.uid || "",
        customer_name: customerName,
        customer_phone: customerPhone,
        passengers,

        pickup,
        drop,
        dropState: drop,

        pickup_lat: latitude,
        pickup_lng: longitude,

        drop_lat: drop_latitude,
        drop_lng: drop_longitude,

        isScheduled,
        pickupTime: pickupTimestamp || serverTimestamp(),

        vehicle_id: null,
        NoOfDays: 1,
        vehicle_type: selectedCar.id,
        total_price:
          selectedCar.minprice !== 0
            ? "₹" +
              parseFloat(selectedCar.minprice).toFixed(2) +
              " - " +
              "₹" +
              parseFloat(selectedCar.price).toFixed(2)
            : parseFloat(selectedCar.price).toFixed(0),
        confirm_price: selectedCar.minprice !== 0 ? 1 : 0,
        km_price: 0.0,
        daily_rate: 0.0,
        // ✅ Safe Payment Fields
        prebookingFeePaid: isScheduled ? !!paymentRef : false,
        prebookingFeeAmount: isScheduled && paymentRef ? 200 : 0,
        razorpay_payment_ref: paymentRef || "",

        status: 0,
        driver_id: null,
        created_at: serverTimestamp(),
        booking_type: "Pick & Drop",
      };

      // console.log(bookingData, 'booking')
      await addDoc(collection(db, "LiveBookings"), bookingData);

      // ✅ Send SMS to admin
      // sendSMSReminder(bookingData);
      
      setTimeout(() => {
        navigate("/my-ride-details");
        toast.success("Booking Confirmed!");
      }, 1000);
    } catch (err) {
      console.error("Booking error:", err);
      toast.error("Booking failed");
      setSavingBooking(false);
    }

   
  };

  function FitBounds({ coords }) {
    const map = useMap();

    useEffect(() => {
      if (coords && coords.length > 0) {
        setTimeout(() => {
          const bounds = L.latLngBounds(coords);
          map.fitBounds(bounds, { padding: [50, 50] });
        }, 300); // delay is important
      }
    }, [coords, map]);

    return null;
  }

  return (
    <>
      <GoToTop />
      {updatingTrip && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-light bg-opacity-75 d-flex justify-content-center align-items-center z-3 loader">
          <div className="bg-white px-4 py-3 rounded-4 shadow fw-bold">
            Updating route...
          </div>
        </div>
      )}
      {/* MAP */}
      <div style={{ height: "300px", marginTop: "75px", position: "relative" }}>
        {loading && (
          <div className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center bg-light bg-opacity-75">
            Calculating route...
          </div>
        )}

        {pickupCoords && dropCoords && (
          <MapContainer
            center={pickupCoords}
            zoom={13}
            zoomControl={false}
            style={{ height: "100%", width: "100%", zIndex: "10" }}
          >
            <TileLayer
              zIndex={1}
              // attribution="© OpenStreetMap © CARTO"
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />

            {/* Pickup Marker with Floating Label */}
            <Marker
              position={pickupCoords}
              icon={L.divIcon({
                className: "custom-marker pickup-marker",
                iconSize: [20, 20],
                html: `<div></div>`,
              })}
            >
              <Tooltip
                direction="top"
                offset={[0, -10]}
                permanent
                className="custom-tooltip pickup-tooltip"
              >
                <div>
                  <b>Pickup</b>
                  <div>{pickup}</div>
                </div>
              </Tooltip>
            </Marker>

            {/* Drop Marker with Floating Label */}
            <Marker
              position={dropCoords}
              icon={L.divIcon({
                className: "custom-marker drop-marker",
                iconSize: [20, 20],
                html: `<div></div>`,
              })}
            >
              <Tooltip
                direction="top"
                offset={[0, -10]}
                permanent
                className="custom-tooltip drop-tooltip"
              >
                <div>
                  <b>Drop</b>
                  <div>{drop}</div>
                </div>
              </Tooltip>
            </Marker>

            {/* Route */}
            {/* {directionsResult && (
              <>
                <Polyline
                  positions={directionsResult}
                  pathOptions={{ color: "#ffffff", weight: 10 }}
                />
                <Polyline
                  positions={directionsResult}
                  pathOptions={{ color: "#000000", weight: 6 }}
                />
              </>
            )} */}

            {directionsResult && (
              <>
                <Polyline
                  positions={directionsResult}
                  pathOptions={{ color: "black", weight: 5 }}
                />
              </>
            )}

            {directionsResult && <FitBounds coords={directionsResult} />}
          </MapContainer>
        )}
      </div>

      <div className="container py-3">
        {/* MAP */}

        {/* HEADER */}
        <div className="uber-summary-card">
          <div className="uber-locations">
            <div className="location-row">
              <div className="dot pickup-dot"></div>
              <div className="location-text">
                <span className="label">Pickup</span>
                <span className="value">{pickup}</span>
              </div>
            </div>

            <div className="vertical-line"></div>

            <div className="location-row">
              <div className="square drop-square"></div>
              <div className="location-text">
                <span className="label">Drop</span>
                <span className="value">{drop}</span>
              </div>
            </div>
          </div>

          <div className="uber-meta">
            {isScheduled ? (
              <span className="meta-badge">
                Scheduled •{" "}
                {new Date(pickupDate).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                })}{" "}
                •{" "}
                {new Date(`1970-01-01T${pickupTime}`).toLocaleTimeString(
                  "en-IN",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )}
              </span>
            ) : (
              <span className="meta-badge">Instant pickup</span>
            )}

            {distance && duration && (
              <span className="meta-time">
                {distance} km • {formatDuration(duration)}
              </span>
            )}
          </div>

          <FaPencilAlt
            className="edit-icon"
            onClick={() => setShowEditModal(true)}
          />
        </div>

        {/* CAR OPTIONS */}
        <div className="uber-ride-section mt-3">
          <h6 className="uber-title mb-2">Choose a ride</h6>

          {/* Sedan */}
          <div
            className={`uber-ride-card ${
              selectedCar?.type === "economy" ? "active" : ""
            }`}
            onClick={() => handleSelectCar("economy")}
          >
            <div className="ride-left">
              <img
                src={sedan}
                alt="economy"
                style={{ mixBlendMode: "darken" }}
              />
              <div>
                <div className="ride-name">Economy</div>
                <div className="ride-desc">
                  Affordable <RiUser6Line className="icon" />4
                </div>
                {/* <div className="ride-time">2 mins away</div> */}
              </div>
            </div>

            <div className="ride-price">
              {/* {selectedRate ? `₹${selectedRate.sedan}` : "₹0"} */}
              <p className="mb-0">
                {selectedRate && isAirportRoute() ? (
                  formatINR(selectedRate.economy)
                ) : distance ? (
                  <p className="mb-0">
                    {formatINR((distance * min_economyprice).toFixed(0))} -{" "}
                    {formatINR((distance * max_economyprice).toFixed(0))}
                  </p>
                ) : (
                  ""
                )}{" "}
              </p>
            </div>
          </div>

          {/* Standard */}

          <div
            // style={{ cursor: selectedCar?.premium ? "" : "not-allowed" }}
            className={`uber-ride-card ${
              selectedCar?.type === "standard" ? "active" : ""
            }`}
            onClick={() => handleSelectCar("standard")}
          >
            <div className="ride-left">
              <img
                src={standard}
                alt="standard"
                style={{ mixBlendMode: "darken" }}
              />
              <div>
                <div className="ride-name">Standard</div>
                <div className="ride-desc">
                  Comfortable <RiUser6Line className="icon" />
                  6-7
                </div>
                {/* <div className="ride-time">3 mins away</div> */}
              </div>
            </div>

            <div className="ride-price">
              <p className="mb-0">
                {selectedRate && isAirportRoute() ? (
                  `${formatINR(standard_price)}`
                ) : distance ? (
                  <p className="mb-0">
                    {formatINR((distance * min_standardprice).toFixed(0))} -{" "}
                    {formatINR((distance * max_standardprice).toFixed(0))}
                  </p>
                ) : (
                  ""
                )}
              </p>
            </div>
          </div>

          {/* Premium */}

          <div
            // style={{ cursor: selectedCar?.premium ? "" : "not-allowed" }}
            className={`uber-ride-card ${
              selectedCar?.type === "premium" ? "active" : ""
            }`}
            onClick={() => handleSelectCar("premium")}
          >
            <div className="ride-left">
              <img
                src={premium}
                alt="premium"
                style={{ mixBlendMode: "darken" }}
              />
              <div>
                <div className="ride-name">Premium</div>
                <div className="ride-desc">
                  Spacious <RiUser6Line className="icon" />
                  6-7
                </div>
                {/* <div className="ride-time">3 mins away</div> */}
              </div>
            </div>

            <div className="ride-price">
              {/* {selectedRate?.premium ? `₹${selectedRate.premium}` : "₹0.00"} */}
              <p className="mb-0">
                {selectedRate && isAirportRoute() ? (
                  `${formatINR(premium_price)}`
                ) : distance ? (
                  <p className="mb-0">
                    {formatINR((distance * min_premiumprice).toFixed(0))} -{" "}
                    {formatINR((distance * max_premiumprice).toFixed(0))}
                  </p>
                ) : (
                  ""
                )}
              </p>

              {/* {selectedRate?.premium && isAllowedRoute() ? `₹${formatINR(selectedRate.premium)}` : distance ? (`Est.${formatINR(distance*49)}`): ""} */}
            </div>
          </div>

          <div>
            {selectedRate ? null : (
              <>
                <p style={{ fontSize: "12px", color: "red" }} className="mb-0">
                  *The final price will be communicated by our support team.
                </p>
                <p style={{ fontSize: "12px", color: "red" }} className="mb-0">
                  *চূড়ান্ত মূল্য আমাৰ সহায়তা দলে আপোনালোকক জনাব |
                </p>
              </>
            )}
          </div>

          <button
            className="btn btn-dark w-100 py-3 fw-bold uber-book-btn"
            disabled={!selectedCar}
            onClick={() => setShowBookingModal(true)}
          >
            {selectedCar ? `Confirm ${selectedCar.name}` : "Select a ride"}
          </button>
        </div>
      </div>

      <PlanYourTripModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSearchRide={handleEditRide}
        initialPickup={pickup}
        initialDrop={drop}
        initialScheduled={isScheduled}
        initialPickupDate={pickupDate}
        initialPickupTime={pickupTime}
        loading={updatingTrip}
      />

      {showBookingModal && (
        <div
          className="bottom-modal-overlay"
          onClick={() => setShowBookingModal(false)}
        >
          <div className="bottom-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header mb-2">
              <h5 className="fw-bold mb-0">Confirm Booking</h5>
              <button
                className="btn-close"
                onClick={() => setShowBookingModal(false)}
              />
            </div>

            <div className="uber-summary-card mb-2">
              <div className="uber-locations">
                <div className="location-row">
                  <div className="dot pickup-dot"></div>
                  <div className="location-text">
                    <span className="label">Pickup</span>
                    <span className="value">{pickup}</span>
                  </div>
                </div>

                <div className="short-vertical-line"></div>

                <div className="location-row">
                  <div className="square drop-square"></div>
                  <div className="location-text">
                    <span className="label">Drop</span>
                    <span className="value">{drop}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="uber-ride-card mb-2">
              {/* LEFT SECTION */}
              <div className="ride-left">
                <img
                  src={selectedCar.img_url}
                  alt="vehicle"
                  className="uber-vehicle-img"
                />

                <div>
                  <div className="ride-name">{selectedCar.name}</div>
                  <div className="ride-desc">
                    {selectedCar.desc} <RiUser6Line className="icon" />
                    {selectedCar.seat}
                  </div>
                  {/* <p className="text-muted small mb-0">
                      {selectedCar.seat} seats • AC
                    </p> */}
                </div>
              </div>

              {/* RIGHT SECTION (PRICE) */}
              <div className="ride-price">
                <p className="mb-0">
                  {selectedCar.minprice != 0 ? (
                    <>{formatINR(selectedCar.minprice)} - </>
                  ) : null}{" "}
                  {formatINR(selectedCar.price)}{" "}
                  {/* <small
                    style={{
                      fontSize: "8.5px",
                      color: "grey",
                      fontWeight: "400",
                    }}
                  >
                    +GST
                  </small> */}
                </p>
                <div className="text-muted small" style={{ fontSize: "12px" }}>
                  Estimated fare
                </div>
              </div>
            </div>

            <p className="badge badge-dark mb-0 p-2">
              {isScheduled ? (
                <p className="mb-0">
                  🕒 Scheduled:{" "}
                  {new Date(pickupDate).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                  })}{" "}
                  at{" "}
                  {new Date(`1970-01-01T${pickupTime}`).toLocaleTimeString(
                    "en-IN",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )}
                </p>
              ) : (
                <p className="mb-0">⚡ Instant Pickup</p>
              )}
            </p>

            <div>
              <label className="form-label">Passenger Name</label>
              <input
                type="text"
                required
                className="modal-input mt-0 mb-1"
                placeholder="Customer Name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />

              <label className="form-label">
                Mobile Number<span style={{ color: "red" }}>*</span>
              </label>
              <div className="d-flex gap-1">
                <input
                  type="tel"
                  className="modal-input mt-0 mb-1"
                  placeholder="Mobile Number"
                  value={customerPhone}
                  readOnly={user}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  disabled={phoneVerified}
                />
                {!otpSent && !phoneVerified && !user && (
                  <button
                    className="btn btn-dark"
                    onClick={sendOTP}
                    style={{
                      width: "180px",
                      height: "50px",
                      borderRadius: "10px",
                    }}
                    disabled={loadingOTP}
                  >
                    {loadingOTP ? "Sending..." : "Send OTP"}
                  </button>
                )}
              </div>

              {otpSent &&
                !phoneVerified &&
                (timer > 0 ? (
                  <p
                    style={{ fontSize: "13px", color: "red" }}
                    className="mb-0 ms-2"
                  >
                    Resend in {timer}s
                  </p>
                ) : (
                  <p
                    className="mb-0 ms-2"
                    onClick={!loadingOTP ? sendOTP : null}
                    style={{
                      fontSize: "13px",
                      color: "red",
                      cursor: loadingOTP ? "not-allowed" : "pointer",
                      opacity: loadingOTP ? 0.5 : 1,
                    }}
                  >
                    Resend OTP
                  </p>
                ))}

              <div id="recaptcha-container"></div>

              {/* OTP Section */}
              {otpSent && !phoneVerified && (
                <div className="mt-2">
                  <div className="d-flex gap-2">
                    <input
                      type="number"
                      className="modal-input mt-0"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />

                    <button
                      className="btn btn-dark"
                      style={{
                        width: "180px",
                        height: "50px",
                        borderRadius: "10px",
                      }}
                      disabled={!confirmationResult || loadingVerify}
                      onClick={verifyOTP}
                    >
                      {loadingVerify ? "Verifying..." : "Verify OTP"}
                    </button>
                  </div>
                </div>
              )}

              <label className="form-label">Number of Passengers</label>

              <div className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                <span>Passengers</span>

                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark rounded-circle px-2"
                    onClick={() =>
                      setPassengers((prev) => Math.max(1, prev - 1))
                    }
                  >
                    −
                  </button>

                  <span className="fw-bold">{passengers}</span>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark rounded-circle px-2"
                    onClick={() => setPassengers((prev) => prev + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            {/* {isScheduled == true && (
              <div className="card mt-2 p-3" style={{ marginBottom: "5px" }}>
                <div className="d-flex justify-content-between mb-0">
                  <small className="mb-0">Booking Fee :</small>
                  <b className="mb-0 text-success">₹200.00</b>
                </div>
              </div>
            )} */}

            
            
            
            <div className="mt-3">

                <button
                  className="btn btn-dark w-100 py-3 fw-bold"
                  disabled={!user || savingBooking}
                  onClick={handleConfirmBooking}
                >
                  {savingBooking
                    ? "Saving..."
                    : !user
                      ? "Login to Confirm Booking"
                      : "Confirm Booking"}
              </button>
              
              {/* {isScheduled == true ? (
                <button
                  className="btn btn-dark w-100 py-3 fw-bold"
                  disabled={!user || savingBooking}
                  onClick={handlePreBookingPayment}
                >
                  {savingBooking
                    ? "Confirming payment..."
                    : !user
                      ? "Login to Confirm Pre-Booking"
                      : "Proceed to Pay"}
                </button>
              ) : (
                <button
                  className="btn btn-dark w-100 py-3 fw-bold"
                  disabled={!user || savingBooking}
                  onClick={handleConfirmBooking}
                >
                  {savingBooking
                    ? "Saving..."
                    : !user
                      ? "Login to Confirm Booking"
                      : "Confirm Booking"}
                </button>
              )} */}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ChooseCar;
